import { useState, useEffect } from 'react';
import {
  Breadcrumb,
  Layout,
  Menu,
  theme,
  Input,
  Modal,
  Radio,
  Button,
  DatePicker,
  InputNumber,
} from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { Space, Tooltip } from 'antd';
import { getAuth, signOut } from 'firebase/auth';
import {
  BrowserRouter,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import UserMenu from './UserMenu';
import dayjs from 'dayjs';

const { Header, Content, Footer } = Layout;
const { RangePicker } = DatePicker;

export const DEFAULT_START_DATE = '1940-01-01';
export const DEFAULT_END_DATE = '2005-12-31';
export const MIN_DATE = '1915-01-01';
export const MAX_DATE = '2005-12-31';

// Semantic (vector) search: cosine distance threshold. 0 = identical meaning,
// higher = looser. Only results closer than this are returned.
export const DEFAULT_SEMANTIC_THRESHOLD = 0.6;

const items = [
  {
    label: 'Home',
    key: 'home',
  },
  {
    label: 'Favorites',
    key: 'favorites',
  },
];

const AntLayout = ({ children }) => {
  const params = new URLSearchParams(window.location.search);
  const initialSearchTerm = params.get('searchTerm');

  const Search = Input.Search;

  const [current, setCurrent] = useState('home');
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchType, setSearchType] = useState('exact');
  const [threshold, setThreshold] = useState(DEFAULT_SEMANTIC_THRESHOLD);
  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem('searchDateRange');
    if (saved) {
      return JSON.parse(saved);
    }
    return [DEFAULT_START_DATE, DEFAULT_END_DATE];
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (isModalOpen) {
      const timer = setTimeout(() => {
        document.querySelector('.ant-modal input')?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isModalOpen]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onSearchClick = (searchTerm) => {
    let url = `/search?searchTerm=${encodeURIComponent(
      searchTerm,
    )}&searchType=${searchType}`;
    if (searchType === 'semantic') {
      url += `&threshold=${threshold}`;
    }
    navigate(url);
    setIsModalOpen(false);
  };

  const onMenuClick = (e) => {
    console.log('click ', e);
    setCurrent(e.key);
    navigate(`/${e.key}`);
  };

  const handleDateRangeChange = (dates) => {
    if (!dates) return;
    const [start, end] = dates;
    const newRange = [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
    setDateRange(newRange);
    localStorage.setItem('searchDateRange', JSON.stringify(newRange));

    // If we're on the search page, update the URL with current search params
    if (window.location.pathname === '/search') {
      const currentParams = new URLSearchParams(window.location.search);
      let url = `/search?searchTerm=${currentParams.get(
        'searchTerm',
      )}&searchType=${currentParams.get('searchType')}`;
      if (currentParams.get('threshold')) {
        url += `&threshold=${currentParams.get('threshold')}`;
      }
      navigate(url, { replace: true });
    }
  };

  return (
    <Layout>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          width: '100%',
          backgroundColor: 'white',
          boxShadow: '0px 0px 8px rgba(0,0,0,.4)',
          paddingLeft: '20px',
          paddingRight: '20px',
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}>
          <div
            style={{
              fontSize: '1.5em',
              fontWeight: 'bold',
              marginLeft: '0px',
              marginRight: '30px',
            }}>
            News Archive
          </div>
          <Menu
            onClick={onMenuClick}
            selectedKeys={[current]}
            mode="horizontal"
            items={items}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <RangePicker
            value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
            onChange={handleDateRangeChange}
            disabledDate={(current) => {
              const date = current.format('YYYY-MM-DD');
              return date < MIN_DATE || date > MAX_DATE;
            }}
            style={{ marginRight: 10 }}
          />
          <Search
            placeholder="type here"
            style={{ width: 200, marginRight: 10 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={onSearchClick}
            onClick={showModal}
            readOnly
          />
          <UserMenu />
        </div>
      </Header>
      <Modal
        title="Advanced Search"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Search
            autoFocus
            placeholder="Enter search term"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={onSearchClick}
            enterButton="Search"
            size="large"
          />

          <Radio.Group
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Radio value="broadest">Broadest search (regex)</Radio>
            <Radio value="broad">Broad search (ilike)</Radio>
            <Radio value="exact">Word match (with stemming)</Radio>
            <Radio value="strictExact">Strict word match (no stemming)</Radio>
            <Radio value="semantic">Semantic search (AI embeddings)</Radio>
          </Radio.Group>

          {searchType === 'semantic' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
              <span>Max distance:</span>
              <InputNumber
                min={0}
                max={2}
                step={0.05}
                value={threshold}
                onChange={(v) =>
                  setThreshold(v ?? DEFAULT_SEMANTIC_THRESHOLD)
                }
              />
              <span style={{ color: '#888', fontSize: '0.85em' }}>
                0 = identical meaning, higher = looser matches
              </span>
            </div>
          )}
        </div>
      </Modal>
      <Content className="site-layout" style={{ padding: '0px' }}>
        {children}
      </Content>
    </Layout>
  );
};

export default AntLayout;
